import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { HandHeart, FileText, Heart, ArrowRight, MapPin } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import StarField from "@/components/StarField";
import FundCriteria from "@/components/FundCriteria";
import ShopSection from "@/components/ShopSection";
import { fundApi, paymentsApi, formatApiError } from "@/lib/api";

const URGENCY_LABEL = {
    niedrig: "Niedrig",
    normal: "Normal",
    hoch: "Hoch",
    akut: "Akut",
};

export default function Fund() {
    const [params] = useSearchParams();
    const [stats, setStats] = useState(null);
    const [cases, setCases] = useState([]);
    const [donation, setDonation] = useState("10");
    const [donorName, setDonorName] = useState("");
    const [donorEmail, setDonorEmail] = useState("");
    const [donating, setDonating] = useState(false);
    const [polling, setPolling] = useState(false);

    useEffect(() => {
        fundApi.stats().then(setStats).catch(() => {});
        fundApi.cases().then((d) => setCases(d.cases || [])).catch(() => {});
    }, []);

    // Handle return from Stripe donation OR shop order
    useEffect(() => {
        const sid = params.get("session_id");
        if (!sid) return;
        const isShop = params.get("kind") === "shop";
        setPolling(true);
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const r = await paymentsApi.checkoutStatus(sid);
                if (r.status === "paid") {
                    if (isShop || r.kind === "shop_order") {
                        toast.success("Danke für deine Bestellung! Wir melden uns mit Versanddetails.");
                    } else {
                        toast.success(`Vielen Dank für deine Spende von ${r.amount_eur} €`);
                    }
                    setPolling(false);
                    fundApi.stats().then(setStats);
                    return;
                }
                if (r.status === "expired" || r.status === "failed") {
                    toast.error(isShop ? "Bestellung nicht abgeschlossen" : "Spende nicht abgeschlossen");
                    setPolling(false);
                    return;
                }
                if (attempts >= 8) {
                    setPolling(false);
                    return;
                }
                setTimeout(poll, 1800);
            } catch {
                setPolling(false);
            }
        };
        poll();
    }, [params]);

    async function donate(e) {
        e.preventDefault();
        const amount = parseFloat(donation);
        if (isNaN(amount) || amount < 1) return toast.error("Mindestens 1 €");
        setDonating(true);
        try {
            const r = await paymentsApi.createDonationCheckout(amount, donorName, donorEmail);
            window.location.href = r.url;
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setDonating(false);
        }
    }

    return (
        <SiteLayout>
            <section className="relative">
                <StarField density={50} className="absolute inset-0" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
                    <div className="label mb-4">Himmelsfond</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl">
                        Aus jeder Erinnerung wächst{" "}
                        <span className="italic text-[#f0c674]">konkrete Hilfe</span> für eine
                        lebende Taube.
                    </h1>
                    <p className="text-[#94a3b8] text-lg mt-6 max-w-2xl leading-relaxed">
                        Pro Eintrag im Taubenhimmel fließt 1,00 € direkt in den Fonds. Jede
                        Spende landet — abzüglich der Zahlungsgebühren — vollständig im
                        Fonds. Transparent, nachvollziehbar, zweckgebunden.
                    </p>
                </div>
            </section>

            {polling && (
                <div className="max-w-3xl mx-auto px-4 mt-4">
                    <div className="glass p-6 text-center" data-testid="fund-polling">
                        <div className="label gentle-pulse">Spende wird verbucht…</div>
                    </div>
                </div>
            )}

            {/* COUNTERS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    <StatCard label="Im Fonds gesammelt" value={stats ? stats.collected_eur : null} suffix="€" highlight testid="stat-collected" />
                    <StatCard label="Ausgezahlt" value={stats ? stats.paid_out_eur : null} suffix="€" testid="stat-paid" />
                    <StatCard label="Aktueller Bestand" value={stats ? stats.balance_eur : null} suffix="€" testid="stat-balance" />
                    <StatCard label="Unterstützte Fälle" value={stats ? stats.supported_cases : null} testid="stat-cases" />
                </div>
            </section>

            {/* CTA BANDS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="glass p-8">
                    <FileText size={24} className="text-[#f0c674] mb-4" />
                    <h3 className="font-display text-2xl mb-3">Hilfe beantragen</h3>
                    <p className="text-[#94a3b8] mb-6 leading-relaxed">
                        Du kümmerst dich um eine kranke oder verletzte Stadttaube und brauchst
                        Unterstützung für Tierarztkosten?
                    </p>
                    <Link to="/himmelsfonds/hilfe" className="btn-primary" data-testid="fund-apply-link">
                        Fall einreichen <ArrowRight size={14} />
                    </Link>
                </div>
                <form onSubmit={donate} className="glass p-8" data-testid="fund-donate-form">
                    <Heart size={24} className="text-[#f0c674] mb-4" />
                    <h3 className="font-display text-2xl mb-3">In den Fonds spenden</h3>
                    <p className="text-[#94a3b8] mb-5 leading-relaxed">
                        Dein Beitrag landet — abzüglich der Zahlungsgebühren — direkt
                        bei den Tauben, die ihn brauchen.
                    </p>
                    <div className="flex gap-2 mb-3 flex-wrap">
                        {[5, 10, 25, 50].map((v) => (
                            <button
                                type="button"
                                key={v}
                                className={`chip ${donation === String(v) ? "active" : ""}`}
                                onClick={() => setDonation(String(v))}
                                data-testid={`donate-preset-${v}`}
                            >
                                {v} €
                            </button>
                        ))}
                    </div>
                    <input
                        type="number"
                        min="1"
                        step="0.5"
                        value={donation}
                        onChange={(e) => setDonation(e.target.value)}
                        className="input-field mb-3"
                        placeholder="Eigener Betrag in €"
                        data-testid="donate-amount"
                    />
                    <input
                        value={donorName}
                        onChange={(e) => setDonorName(e.target.value)}
                        placeholder="Dein Name (optional)"
                        className="input-field mb-3"
                        data-testid="donate-name"
                    />
                    <input
                        type="email"
                        value={donorEmail}
                        onChange={(e) => setDonorEmail(e.target.value)}
                        placeholder="E-Mail (optional, für Quittung)"
                        className="input-field mb-4"
                        data-testid="donate-email"
                    />
                    <button type="submit" className="btn-primary" disabled={donating} data-testid="donate-submit">
                        {donating ? "Bitte warten…" : `${donation || 0} € spenden`}
                    </button>
                </form>
            </section>

            {/* SHOP: Bracelets that fund the cause */}
            <ShopSection />

            {/* CRITERIA — Wie der Himmelsfond hilft */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
                <FundCriteria showLinkToTransparency={false} />
            </section>

            {/* SUPPORTED CASES */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 mb-20">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="label mb-3">Transparenz</div>
                        <h2 className="font-display text-3xl sm:text-4xl">Unterstützte Fälle</h2>
                    </div>
                </div>
                {cases.length === 0 ? (
                    <div className="glass p-10 text-center text-[#94a3b8]">
                        Noch keine öffentlichen Fälle.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {cases.map((c, i) => (
                            <motion.div
                                key={c.id}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.03 }}
                                className="glass p-6"
                                data-testid={`case-${c.id}`}
                            >
                                <div className="flex items-start justify-between gap-4 mb-2">
                                    <h3 className="font-display text-xl">{c.pigeon_name}</h3>
                                    <span
                                        className={`text-xs px-2 py-1 rounded-full ${
                                            c.status === "supported"
                                                ? "bg-emerald-500/10 text-emerald-300"
                                                : "bg-[#f0c674]/10 text-[#f0c674]"
                                        }`}
                                    >
                                        {c.status === "supported" ? "Unterstützt" : "Angenommen"}
                                    </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-[#94a3b8] mb-3">
                                    {c.location && (
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin size={11} /> {c.location}
                                        </span>
                                    )}
                                    {c.urgency && (
                                        <span className="inline-flex items-center gap-1">
                                            Dringlichkeit: {URGENCY_LABEL[c.urgency] || c.urgency}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-[#94a3b8] leading-relaxed line-clamp-3">{c.story}</p>
                                {c.status === "supported" && c.supported_amount_eur > 0 && (
                                    <div className="mt-4 text-xs text-emerald-300 font-mono">
                                        + {c.supported_amount_eur.toFixed(2)} € ausgezahlt
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </section>
        </SiteLayout>
    );
}

function StatCard({ label, value, suffix, highlight, testid }) {
    return (
        <div className={`glass p-6 ${highlight ? "border-[#f0c674]/30" : ""}`} data-testid={testid}>
            <div className="label mb-3">{label}</div>
            <div className="font-display text-4xl sm:text-5xl">
                {value === null || value === undefined ? "—" : Number(value).toLocaleString("de-DE")}
                {suffix && <span className="text-2xl text-[#f0c674] ml-1">{suffix}</span>}
            </div>
        </div>
    );
}
